// Who is playing and what they hold, shared by every view of the web app.
import type { Badges, Catalog, Me, Player, ServerPick } from '../src/api.js';
import type { Fighter } from '../src/content/fighters.js';
import type { LineupSlot, SavedLineup } from '../src/game/lineup.js';
import { Ui } from './ui.js';

const [catalog, me] = await Promise.all([Ui.api<Catalog>('/api/catalog'), Ui.api<Me>('/api/me')]);
const clockOffset = me.serverNow - Date.now();
const serverNow = (): number => Date.now() + clockOffset;

const fightersById = new Map(catalog.fighters.map(w => [w.id, w]));
const fighter = (id: string): Fighter | undefined => fightersById.get(id);

/** A currency amount with its symbol, e.g. "✨ 1,250". */
const coins = (n: number): string => `${catalog.currency.symbol} ${n.toLocaleString()}`;

const setCurrency = (n: number): void => {
  if (!me.player) return;
  me.player.currency = n;
  Ui.el('#balance').textContent = coins(n);
};

const setBadges = (badges: Partial<Badges>): void => {
  Object.assign(me.badges, badges);
  Ui.el('#navTournamentBadge').hidden = !me.badges.canJoinTournament;
  const duels = Ui.el('#navDuelBadge');
  duels.hidden = !me.badges.incomingDuels;
  duels.textContent = String(me.badges.incomingDuels);
};

/** Everything on screen belongs to one server, so switching starts the app over. */
const switchServer = async (serverId: string): Promise<void> => {
  const body: ServerPick = { serverId };
  await Ui.api('/api/server', { method: 'POST', body });
  location.reload();
};

const ACTIVE_LINEUP_KEY = 'activeLineup';

const storedLineupId = (): number | null => {
  try {
    return Number(localStorage.getItem(ACTIVE_LINEUP_KEY)) || null;
  } catch {
    return null;
  }
};

/** The lineup last opened or picked. Editing, training, tournaments and duels all start from it. */
let activeLineupId = storedLineupId();

const setActiveLineup = (id: number | null): void => {
  activeLineupId = id;
  try {
    if (id !== null) localStorage.setItem(ACTIVE_LINEUP_KEY, String(id));
  } catch {
    // Private windows can refuse storage; the pick then lasts until reload.
  }
};

/** The active lineup, or the player's first one. */
const activeLineup = (player: Player): SavedLineup | undefined =>
  player.lineups.find(l => l.id === activeLineupId) ?? player.lineups[0];

/** Slots whose fighter is still in the roster. */
const playable = (l: SavedLineup): LineupSlot[] => l.lineup.filter(s => fightersById.has(s.fighterId));

const hasPlayableLineup = (player: Player): boolean => {
  const picked = activeLineup(player);
  return !!picked && playable(picked).length > 0;
};

export const Session = {
  catalog,
  me,
  serverNow,
  fighter,
  coins,
  setCurrency,
  setBadges,
  switchServer,
  activeLineup,
  setActiveLineup,
  playable,
  hasPlayableLineup,
};
